import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "—");

const Cifra = ({ valor, label, testid }) => (
  <div className="bg-[#FAF8F5] rounded-xl p-3" data-testid={testid}>
    <p className="font-heading text-2xl font-bold text-[#1C2421]">{valor}</p>
    <p className="text-sm text-[#6E7A74] leading-tight">{label}</p>
  </div>
);

const Resumen = ({ r }) => (
  <div className="grid grid-cols-2 gap-2 mt-4" data-testid="actividad-resumen">
    <Cifra testid="actividad-registrados" valor={r.registrados} label="Registrados" />
    <Cifra testid="actividad-han-entrado" valor={r.han_entrado} label="Han iniciado sesión" />
    <Cifra testid="actividad-7d" valor={r.activos_7d} label="Activos últimos 7 días" />
    <Cifra testid="actividad-30d" valor={r.activos_30d} label="Activos últimos 30 días" />
    <Cifra testid="actividad-activados" valor={`${r.activados} · ${r.pct_activados}%`} label="Activados (3+ acciones)" />
    <Cifra testid="actividad-vuelven" valor={`${r.pct_vuelven}%`} label="Vuelven otro día" />
    <Cifra testid="actividad-usa-funcion" valor={`${r.pct_usa_funcion}%`} label="Han usado una función" />
  </div>
);

const Funciones = ({ lista, total }) => (
  <div className="mt-5" data-testid="actividad-funciones">
    <p className="font-semibold text-[#1C2421]">Funciones más utilizadas</p>
    <ul className="mt-2 space-y-2">
      {lista.map((f) => (
        <li key={f.clave} data-testid={`actividad-funcion-${f.clave}`}>
          <div className="flex justify-between text-sm"><span className="text-[#1C2421]">{f.nombre}</span><span className="text-[#6E7A74]">{f.usuarios} {f.usuarios === 1 ? "usuario" : "usuarios"} · {f.total}</span></div>
          <div className="h-2 rounded-full bg-[#F2EDE4] mt-1"><div className="h-2 rounded-full bg-[#3A6B56]" style={{ width: `${total ? (100 * f.usuarios) / total : 0}%` }} /></div>
        </li>
      ))}
    </ul>
  </div>
);

const Usuario = ({ u, funciones, i }) => {
  const [abierto, setAbierto] = useState(false);
  return (
    <li className="py-3 border-b border-[#F2EDE4] last:border-0" data-testid={`actividad-usuario-${i}`}>
      <button onClick={() => setAbierto((v) => !v)} className="w-full text-left flex items-start gap-2" data-testid={`actividad-usuario-toggle-${i}`}>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#1C2421] truncate">{u.nombre || "Sin nombre"} {u.activado && <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-bold bg-[#E8F0EC] text-[#2E5645]">Activado</span>}</p>
          <p className="text-sm text-[#6E7A74] truncate">{u.email}</p>
          <p className="text-xs text-[#9AA39D] mt-0.5">Último acceso {fecha(u.ultimo_acceso)} · {u.dias_activos} {u.dias_activos === 1 ? "día activo" : "días activos"}</p>
        </div>
        {abierto ? <Icons.ChevronUp size={18} className="text-[#9AA39D] mt-1" /> : <Icons.ChevronDown size={18} className="text-[#9AA39D] mt-1" />}
      </button>
      {abierto && (
        <div className="mt-2 text-sm text-[#4A5550] space-y-1" data-testid={`actividad-usuario-detalle-${i}`}>
          <p>Registro: {fecha(u.registro)} · Sesiones aprox.: {u.sesiones} · Acciones relevantes: {u.acciones_relevantes}</p>
          <ul className="grid grid-cols-1 gap-1 mt-1">
            {funciones.map((f) => (
              <li key={f.clave} className="flex items-center gap-2">
                {u.acciones[f.clave] ? <Icons.CheckCircle2 size={16} className="text-[#3A6B56]" /> : <Icons.Circle size={16} className="text-[#D6CFC2]" />}
                <span>{f.nombre}{u.acciones[f.clave] ? ` (${u.acciones[f.clave]})` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

// Perfil › Actividad de la beta (solo administrador): métricas de uso, sin contenido privado.
export const PanelActividad = () => {
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { if (abierto && !datos) api.get("/admin/actividad").then((r) => setDatos(r.data)).catch(() => setError(true)); }, [abierto, datos]);
  return (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm" data-testid="panel-actividad">
      <button data-testid="panel-actividad-toggle" onClick={() => setAbierto((v) => !v)} className="w-full flex items-center gap-2 text-left">
        <Icons.BarChart3 size={20} className="text-[#3A6B56]" />
        <span className="flex-1 font-heading text-lg font-bold text-[#1C2421]">Actividad de la beta</span>
        {abierto ? <Icons.ChevronUp size={20} className="text-[#9AA39D]" /> : <Icons.ChevronDown size={20} className="text-[#9AA39D]" />}
      </button>
      {abierto && (error ? <p className="text-base text-[#B91C1C] mt-3">No se pudo cargar la actividad.</p>
        : !datos ? <p className="text-base text-[#6E7A74] mt-3">Cargando…</p>
        : <>
          <Resumen r={datos.resumen} />
          <Funciones lista={datos.funciones} total={datos.resumen.registrados} />
          <p className="font-semibold text-[#1C2421] mt-5">Usuarios</p>
          <ul className="mt-1" data-testid="actividad-usuarios">{datos.usuarios.map((u, i) => <Usuario key={u.email} u={u} i={i} funciones={datos.funciones} />)}</ul>
          <p className="text-xs text-[#9AA39D] mt-4">Solo se cuentan acciones; nunca se muestra el contenido de documentos, conversaciones o gastos. "Fotos" incluye cualquier imagen subida.</p>
        </>)}
    </div>
  );
};
